<template>
  <div>
    <div v-if="!feedEntry">
      <div>
        <v-btn
          color="success"
          @click="createFeedEntry"
        >
          <i class="fas fa-plus mr-2" /> Neuer Feed
        </v-btn>
      </div>
    </div>
    <div v-else>
      <b-container fluid>
        <b-row>
          <b-col cols="1">
            <label class="font-weight-bold mr-4">Publiziert</label>
          </b-col>
          <b-col cols="3">
            <u-date-picker
              :value="feedEntry.publicationDate"
              label="Publiziert"
              @change="setFeedEntryPublicationDate"
            />
          </b-col>
          <b-col cols="7" />
          <b-col cols="1">
            <v-btn
              color="error"
              small
              fab
              @click="deleteFeedEntry"
            >
              <i class="fas fa-trash" />
            </v-btn>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="1">
            <label class="font-weight-bold mr-5 mt-4">Status</label>
          </b-col>
          <b-col>
            <v-radio-group
              :value="feedEntry.status"
              @change="setFeedEntryStatus"
            >
              <v-radio
                v-for="n in status"
                :key="n.value"
                :label="n.text"
                :value="n.value"
                :disabled="n.value === 2"
              />
            </v-radio-group>
          </b-col>
        </b-row>
        <b-row v-if="feedContent_de">
          <b-col cols="1">
            <label class="font-weight-bold mr-5">Text</label>
          </b-col>
          <b-col>
            <label class="font-weight-bold">
              {{ getLang(feedContent_de.language) }}</label>
            <v-text-field
              v-if="canUserEdit"
              :value="feedContent_de.value"
              :label="getLang(feedContent_de.language)"
              @change="setEntryLangContent($event, feedContent_de.language)"
            />
          </b-col>
        </b-row>
        <b-row v-if="feedContent_fr">
          <b-col cols="1" />
          <b-col>
            <label class="font-weight-bold">
              {{ getLang(feedContent_fr.language) }}</label>
            <v-text-field
              v-if="canUserEdit"
              :value="feedContent_fr.value"
              :label="getLang(feedContent_fr.language)"
              @change="setEntryLangContent($event, feedContent_fr.language)"
            />
          </b-col>
        </b-row>
        <b-row v-if="feedContent_it">
          <b-col cols="1" />
          <b-col>
            <label class="font-weight-bold">
              {{ getLang(feedContent_it.language) }}</label>
            <v-text-field
              v-if="canUserEdit"
              :value="feedContent_it.value"
              :label="getLang(feedContent_it.language)"
              @change="setEntryLangContent($event, feedContent_it.language)"
            />
          </b-col>
        </b-row>
      </b-container>
    </div>
  </div>
</template>

<script>
import UDatePicker from "@/components/common/UDatePicker.vue";
import {
  GetPublicationFeedEntry,
  CreateNewEntry,
  DeleteFeedEntry,
} from "@/services/rssFeed.service";

export default {
  name: "TenantWhatsNewSection",
  components: { UDatePicker },
  props: {
    contractId: { type: String, default: "" },
    versionId: { type: Number, default: 0 },
    tenant: { type: Object, default: () => undefined },
    canUserEdit: {
      type: Boolean,
      default: false,
    },
    langFilter: {
      type: Array,
      default: () => [
        { ref: "de-CH", name: "Deutsch" },
        { ref: "fr-CH", name: "Französisch" },
        { ref: "it-CH", name: "Italienisch" },
      ],
    },
  },
  data() {
    return {
      feedEntry: {},
      isLoading: false,
      errorObject: null,
      selectedTenants: [],
      status: [
        { value: 1, text: "Entwurf" },
        { value: 2, text: "Publiziert" },
      ],
    };
  },
  computed: {
    feedContent_de() {
      if (this.langFilter.some((lang) => lang.ref === "de-CH")) {
        return this.getEntryLangContent("de-CH");
      }
      return undefined;
    },
    feedContent_fr() {
      if (this.langFilter.some((lang) => lang.ref === "fr-CH")) {
        return this.getEntryLangContent("fr-CH");
      }
      return undefined;
    },
    feedContent_it() {
      if (this.langFilter.some((lang) => lang.ref === "it-CH")) {
        return this.getEntryLangContent("it-CH");
      }
      return undefined;
    },
  },
  created() {
    this.getFeedEntry();
  },
  methods: {
    createFeedEntry() {
      let contents = [];
      if (this.tenant && this.tenant.publicationContents) {
        contents = this.tenant.publicationContents.map((content) => {
          return {
            language: content.language,
            value: content.news,
          };
        });
      } else {
        contents = this.langFilter.map((lang) => {
          return {
            language: lang.ref,
          };
        });
      }

      const localPublicationDate = this.tenant.publicationDate ? new Date(this.tenant.publicationDate) : null;
      if (localPublicationDate) {
        localPublicationDate.setMinutes(
          localPublicationDate.getMinutes() - localPublicationDate.getTimezoneOffset(),
        );
      }

      let newFeedEntry = {
        publicationId: this.tenant.id,
        publicationDate: localPublicationDate
          ? localPublicationDate.toISOString()
          : null,
        status: 1,
        feedEntryContents: contents,
        tenants: [this.tenant.tenantId],
      };

      this.isLoading = true;
      CreateNewEntry(
        newFeedEntry,
        (response) => {
          this.feedEntry = response;
          this.isLoading = false;
        },
        (error) => {
          this.errorObject = error;
          this.isLoading = false;
        },
      );
    },
    getFeedEntry() {
      if (!this.tenant || !this.tenant.id) return;
      GetPublicationFeedEntry(
        this.tenant.id,
        (response) => {
          this.feedEntry = response;
        },
        () => {
          this.feedEntry = undefined;
        },
      );
    },
    getEntryLangContent(lang) {
      if (!this.feedEntry || !this.feedEntry.feedEntryContents)
        return undefined;
      let content = this.feedEntry.feedEntryContents.find(
        (content) => content.language === lang,
      );
      return content;
    },
    setEntryLangContent(value, lang) {
      let contentIndex = this.feedEntry.feedEntryContents.findIndex(
        (content) => content.language === lang,
      );
      this.feedEntry = {
        ...this.feedEntry,
        feedEntryContents: [
          ...this.feedEntry.feedEntryContents.slice(0, contentIndex),
          {
            ...this.feedEntry.feedEntryContents[contentIndex],
            value: value,
          },
          ...this.feedEntry.feedEntryContents.slice(contentIndex + 1),
        ],
      };
      this.$emit("updateFeedEntry", this.feedEntry);
    },
    setFeedEntryStatus(status) {
      this.feedEntry = {
        ...this.feedEntry,
        status: status,
      };
      this.$emit("updateFeedEntry", this.feedEntry);
    },
    setFeedEntryPublicationDate(date) {
      this.feedEntry = {
        ...this.feedEntry,
        publicationDate: date,
      };
      this.$emit("updateFeedEntry", this.feedEntry);
    },
    deleteFeedEntry() {
      DeleteFeedEntry(
        this.feedEntry.id,
        () => {
          this.feedEntry = undefined;
        },
        (error) => {
          this.errorObject = error;
        },
      );
    },
    getLang(language) {
      const lang = this.langFilter.find((l) => l.ref === language);
      return lang ? lang.name : "";
    },
    resetForm() {
      this.getPublicationsSrc();
      this.selectedTenants = [];
    },
  },
};
</script>

<style scoped></style>
